import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { Card, Table, Pagination, PageHeader, Badge, FilterBar, FilterSelect } from '../components/UI';
import { fmt } from '../utils/helpers';

const METHOD_COLOR = { POST:'green', GET:'blue', PATCH:'yellow', DELETE:'red', PUT:'purple' };

export default function AdminLogs() {
  const [logs, setLogs]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter]   = useState('all');
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit:30 };
      if (filter!=='all') params.action = filter;
      const r = await api.getAdminLogs(params);
      setLogs(r.logs||[]); setTotal(r.total||0);
    } catch(e) { toast(e.message,'error'); }
    finally { setLoading(false); }
  },[page, filter]);

  useEffect(()=>{ load(); },[load]);

  const columns = [
    { key:'time',   label:'Time',   render: l=><span style={{ fontSize:11, color:'var(--text3)', fontFamily:'Geist Mono,monospace', whiteSpace:'nowrap' }}>{fmt.datetime(l.created_at)}</span> },
    { key:'admin',  label:'Admin',  render: l=><span style={{ fontSize:12, fontWeight:500 }}>{l.admin_email||'—'}</span> },
    { key:'method', label:'Method', render: l=><Badge color={METHOD_COLOR[l.method]||'gray'}>{l.method}</Badge> },
    { key:'path',   label:'Path',   render: l=><code style={{ fontSize:11, fontFamily:'Geist Mono,monospace', color:'var(--accent)', wordBreak:'break-all' }}>{l.path}</code> },
    { key:'action', label:'Action', render: l=><span style={{ fontSize:12, color:'var(--text2)' }}>{l.action||'—'}</span> },
    { key:'status', label:'Status', render: l=><Badge color={l.status_code>=400?'red':'green'}>{l.status_code}</Badge> },
    { key:'ip',     label:'IP',     render: l=><span style={{ fontSize:11, fontFamily:'Geist Mono,monospace', color:'var(--text3)' }}>{l.ip_address||'—'}</span> },
    { key:'target', label:'Target', render: l=> l.target_user_email ? <span style={{ fontSize:12 }}>{l.target_user_email}</span> : <span style={{ color:'var(--text3)' }}>—</span> },
  ];

  const mobileCard = (l) => (
    <>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
        <div>
          <div style={{ fontSize:12, fontWeight:600 }}>{l.admin_email||'—'}</div>
          <div style={{ fontSize:10, color:'var(--text3)' }}>{fmt.datetime(l.created_at)}</div>
        </div>
        <div style={{ display:'flex', gap:4 }}>
          <Badge color={METHOD_COLOR[l.method]||'gray'}>{l.method}</Badge>
          <Badge color={l.status_code>=400?'red':'green'}>{l.status_code}</Badge>
        </div>
      </div>
      <code style={{ fontSize:11, color:'var(--accent)', fontFamily:'Geist Mono,monospace', wordBreak:'break-all', display:'block', marginBottom:4 }}>{l.path}</code>
      {l.target_user_email && <div style={{ fontSize:11, color:'var(--text3)' }}>→ {l.target_user_email}</div>}
    </>
  );

  return (
    <div className="page-enter">
      <PageHeader title="Audit Log" subtitle="Every admin action is logged here for accountability" />

      <FilterBar>
        <FilterSelect value={filter} onChange={v=>{setFilter(v);setPage(1);}} options={[
          {value:'all',label:'All Actions'},
          {value:'login',label:'Logins'},
          {value:'fund_wallet',label:'Wallet Funding'},
          {value:'grant_subscription',label:'Plan Grants'},
          {value:'resolve_transaction',label:'Tx Resolves'},
          {value:'toggle_user',label:'Status Changes'},
          {value:'delete_user',label:'User Deletions'},
          {value:'revoke_device',label:'Device Revocations'},
          {value:'config_update',label:'Config Updates'},
        ]} />
      </FilterBar>

      <Card style={{ padding:0, overflow:'hidden' }}>
        <Table columns={columns} rows={logs} loading={loading} emptyText="No audit logs found." mobileCard={mobileCard} />
        <div style={{ borderTop:'1px solid var(--border)', padding:'0 14px' }}>
          <Pagination page={page} total={total} limit={30} onChange={setPage} />
        </div>
      </Card>
    </div>
  );
}